// Accounting — Outlet Expenses
//
// CRUD over the per-outlet OutletExpense table (gateway
// /api/store/{code}/accounting/expenses). Staff can record and correct
// expenses; deleting is admin-only (the gateway enforces it too).

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type {
  AccountingMeta,
  ExpenseRow,
  ExpenseInput,
  ExpenseListResult,
} from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

const fmtMoney = (n: number | undefined) => (Number(n) || 0).toFixed(2);

function todayIso(): string {
  const d = new Date();
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

function isoToMs(v: string, endOfDay = false): number {
  if (!v) return 0;
  const d = new Date(`${v}T00:00:00`);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function msToIso(ms: number): string {
  if (!ms) return todayIso();
  const d = new Date(ms);
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

const MONTH_PRESETS = [
  { id: 'this-month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
  { id: 'this-year', label: 'This year' },
  { id: 'custom', label: 'Custom' },
] as const;

interface KeptExpenseView {
  from: string;
  to: string;
  preset: string;
  category: string;
  paidBy: string;
  q: string;
  rows: ExpenseRow[];
  totals: ExpenseListResult['totals'] | null;
  loaded: boolean;
}

@customElement('expense-manage')
export class ExpenseManage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: end; margin: 0.75rem 1rem 0.5rem; flex-shrink: 0; }
    .toolbar .fg { margin: 0; }
    .toolbar input, .toolbar select { min-height: 38px; padding: 7px 10px; border: 1px solid var(--color-border); border-radius: 10px; background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.84rem; }
    .toolbar input[type='date'] { min-width: 140px; }
    .toolbar .grow { flex: 1; min-width: 160px; }
    .table-wrap { flex: 1; min-height: 0; overflow: auto; margin: 0 1rem 0.75rem; border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); }
    thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); box-shadow: inset 0 -1px 0 var(--color-border); border-bottom: none; }
    tfoot td { font-weight: 700; background: #f8fafd; position: sticky; bottom: 0; }
    .actions { display: flex; gap: 0.35rem; justify-content: flex-end; }
    .state { padding: 2rem; text-align: center; color: var(--color-text-secondary); }
    .error { margin: 0 1rem 0.5rem; padding: 0.6rem 0.8rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: none; align-items: center; justify-content: center; z-index: 60; padding: 1rem; }
    .modal-overlay.open { display: flex; }
    .modal { width: min(680px, 100%); max-height: 90vh; overflow: auto; background: var(--color-surface); border-radius: 16px; box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3); }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-body { padding: 1rem 1.1rem; }
    .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .close { border: none; background: none; font-size: 1.35rem; cursor: pointer; color: var(--color-text-secondary); }
    .hint { font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 0.25rem; }
    @media (max-width: 768px) {
      .toolbar { margin: 0.6rem 0.6rem 0.4rem; }
      .table-wrap { margin: 0 0.6rem 0.6rem; }
      table { display: none; }
      .cards { display: flex; flex-direction: column; gap: 0.5rem; padding: 0.6rem; }
    }
    @media (min-width: 769px) { .cards { display: none; } }
    .card { border: 1px solid var(--color-border); border-radius: 12px; padding: 0.65rem 0.75rem; }
    .card .row1 { display: flex; justify-content: space-between; font-weight: 600; }
    .card .row2 { margin-top: 0.2rem; font-size: 0.78rem; color: var(--color-text-secondary); display: flex; justify-content: space-between; gap: 0.5rem; }
    .card .amt { font-variant-numeric: tabular-nums; }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _meta: AccountingMeta | null = null;
  @state() private _loading = false;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _from = todayIso();
  @state() private _to = todayIso();
  @state() private _preset = 'this-month';
  @state() private _category = '';
  @state() private _paidBy = '';
  @state() private _q = '';
  @state() private _rows: ExpenseRow[] = [];
  @state() private _totals: ExpenseListResult['totals'] | null = null;
  @state() private _loaded = false;
  @state() private _editing: ExpenseInput | null = null;
  @state() private _formError = '';

  private _view = new OutletViewCache<KeptExpenseView>(this, 'accounting-expenses', {
    capture: () => ({
      from: this._from,
      to: this._to,
      preset: this._preset,
      category: this._category,
      paidBy: this._paidBy,
      q: this._q,
      rows: this._rows,
      totals: this._totals,
      loaded: this._loaded,
    }),
    restore: (v) => {
      this._from = v.from;
      this._to = v.to;
      this._preset = v.preset;
      this._category = v.category;
      this._paidBy = v.paidBy;
      this._q = v.q;
      this._rows = v.rows;
      this._totals = v.totals;
      this._loaded = v.loaded;
    },
    reset: () => {
      this._from = todayIso();
      this._to = todayIso();
      this._preset = 'this-month';
      this._category = '';
      this._paidBy = '';
      this._q = '';
      this._rows = [];
      this._totals = null;
      this._loaded = false;
    },
  });

  connectedCallback() {
    super.connectedCallback();
    this._applyPreset('this-month');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    // switchTo() returns 'unchanged' | 'restored' | 'fresh' — compare it, never
    // treat it as a boolean: every one of those strings is truthy, and a
    // truthy check here re-runs the loaders on every render (endless loop).
    const switched = this._view.switchTo(this.storeCode);
    if (switched === 'unchanged' && !changed.has('storeCode')) return;
    this._meta = null;
    void this._loadMeta();
    // A restored screen already shows the rows the user left; a fresh outlet
    // (or a snapshot without totals) needs a load.
    if (switched !== 'restored' || !this._loaded || this._totals === null) void this._load();
  }

  private _applyPreset(preset: string) {
    const now = new Date();
    if (preset === 'this-month') {
      this._from = msToIso(new Date(now.getFullYear(), now.getMonth(), 1).getTime());
      this._to = todayIso();
    } else if (preset === 'last-month') {
      this._from = msToIso(new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime());
      this._to = msToIso(new Date(now.getFullYear(), now.getMonth(), 0).getTime());
    } else if (preset === 'this-year') {
      this._from = msToIso(new Date(now.getFullYear(), 0, 1).getTime());
      this._to = todayIso();
    }
    this._preset = preset;
  }

  private async _loadMeta() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    try {
      const res = await api.accountingMeta(this.storeCode);
      if (!this._view.isCurrent(epoch)) return;
      this._meta = res.data;
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Failed to load accounting settings';
    }
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.accountingExpenses(this.storeCode, {
        from: isoToMs(this._from, false),
        to: isoToMs(this._to, true),
        category: this._category || undefined,
        paidBy: this._paidBy || undefined,
        q: this._q.trim() || undefined,
      });
      if (!this._view.isCurrent(epoch)) return;
      this._rows = res.data.rows;
      this._totals = res.data.totals;
      this._loaded = true;
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Failed to load expenses';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private _newExpense() {
    const today = todayIso();
    this._editing = {
      date: isoToMs(today, false),
      categoryId: '',
      categoryName: '',
      description: '',
      payee: '',
      paidById: '',
      paidByName: '',
      amount: 0,
      taxAmount: 0,
      reference: '',
      remark: '',
    };
    this._formError = '';
  }

  private _edit(row: ExpenseRow) {
    this._editing = {
      id: row.id,
      date: row.date,
      categoryId: row.categoryId,
      categoryName: row.categoryName,
      description: row.description,
      payee: row.payee,
      paidById: row.paidByTypeId ? String(row.paidByTypeId) : '',
      paidByName: row.paidByName,
      amount: row.amount,
      taxAmount: row.taxAmount,
      reference: row.reference,
      remark: row.remark,
    };
    this._formError = '';
  }

  private _setField(key: keyof ExpenseInput, value: unknown) {
    if (!this._editing) return;
    this._editing = { ...this._editing, [key]: value } as ExpenseInput;
  }

  private _pickCategory(id: string) {
    if (!this._editing) return;
    const cat = this._meta?.categories.find((c) => c.id === id);
    this._editing = {
      ...this._editing,
      categoryId: id,
      categoryName: cat ? cat.name : id === '__other__' ? '' : this._editing.categoryName,
    };
  }

  private _pickPaidBy(id: string) {
    if (!this._editing) return;
    const pb = this._meta?.payBy.find((p) => p.id === id);
    this._editing = {
      ...this._editing,
      paidById: id,
      paidByName: pb ? pb.name : '',
    };
  }

  private async _save() {
    if (!this._editing) return;
    const e = this._editing;
    if (!e.date || !(e.amount > 0)) {
      this._formError = 'A date and an amount greater than zero are required.';
      return;
    }
    if (!e.categoryName.trim()) {
      this._formError = 'Pick a category or type one under Other.';
      return;
    }
    if (!e.description.trim()) {
      this._formError = 'Description is required.';
      return;
    }
    this._saving = true;
    this._formError = '';
    const epoch = this._view.epoch;
    try {
      await api.accountingExpenseSave(this.storeCode, {
        ...e,
        amount: Number(e.amount) || 0,
        taxAmount: Number(e.taxAmount) || 0,
      });
      if (!this._view.isCurrent(epoch)) return;
      this._editing = null;
      await this._load();
    } catch (err: any) {
      if (this._view.isCurrent(epoch)) this._formError = err?.message || 'Save failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._saving = false;
    }
  }

  private async _delete(row: ExpenseRow) {
    if (!window.confirm(`Delete "${row.description}" (RM ${fmtMoney(row.amount)})?`)) return;
    const epoch = this._view.epoch;
    try {
      await api.accountingExpenseDelete(this.storeCode, row.id);
      if (!this._view.isCurrent(epoch)) return;
      await this._load();
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Delete failed';
    }
  }

  private _renderForm() {
    const e = this._editing!;
    const cats = this._meta?.categories ?? [];
    const payBys = (this._meta?.payBy ?? []).filter((p) => p.kind !== 'receivable');
    const isOther = !!e.categoryId && e.categoryId === '__other__';
    return html`
      <div class="modal-overlay open" @click=${(ev: Event) => { if (ev.target === ev.currentTarget) this._editing = null; }}>
        <div class="modal" @click=${(ev: Event) => ev.stopPropagation()}>
          <div class="modal-header">
            <h3>${e.id ? 'Edit expense' : 'New expense'}</h3>
            <button class="close" @click=${() => (this._editing = null)}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._formError ? html`<div class="error" style="margin:0 0 0.7rem">${this._formError}</div>` : ''}
            <div class="grid">
              <div class="fg">
                <label>Date</label>
                <input type="date" .value=${msToIso(e.date)}
                  @change=${(ev: Event) => this._setField('date', isoToMs((ev.target as HTMLInputElement).value, false))}>
              </div>
              <div class="fg">
                <label>Category</label>
                <select .value=${e.categoryId}
                  @change=${(ev: Event) => this._pickCategory((ev.target as HTMLSelectElement).value)}>
                  <option value="">Select…</option>
                  ${cats.map((c) => html`<option value=${c.id} ?selected=${e.categoryId === c.id}>${c.name}</option>`)}
                  <option value="__other__" ?selected=${isOther}>Other (type below)</option>
                </select>
                ${isOther ? html`
                  <input style="margin-top:0.4rem" placeholder="Category name"
                    .value=${e.categoryName}
                    @input=${(ev: Event) => this._setField('categoryName', (ev.target as HTMLInputElement).value)}>
                ` : ''}
              </div>
              <div class="fg full">
                <label>Description</label>
                <input placeholder="e.g. Shop rental for October" .value=${e.description}
                  @input=${(ev: Event) => this._setField('description', (ev.target as HTMLInputElement).value)}>
              </div>
              <div class="fg">
                <label>Payee (optional)</label>
                <input .value=${e.payee ?? ''}
                  @input=${(ev: Event) => this._setField('payee', (ev.target as HTMLInputElement).value)}>
              </div>
              <div class="fg">
                <label>Paid by</label>
                <select .value=${e.paidById ?? ''}
                  @change=${(ev: Event) => this._pickPaidBy((ev.target as HTMLSelectElement).value)}>
                  <option value="">Select…</option>
                  ${payBys.map((p) => html`<option value=${p.id} ?selected=${e.paidById === p.id}>${p.name}</option>`)}
                </select>
              </div>
              <div class="fg">
                <label>Amount (RM)</label>
                <input type="number" step="0.01" min="0" .value=${String(e.amount ?? 0)}
                  @input=${(ev: Event) => this._setField('amount', Number((ev.target as HTMLInputElement).value))}>
              </div>
              <div class="fg">
                <label>Tax / GST (optional)</label>
                <input type="number" step="0.01" min="0" .value=${String(e.taxAmount ?? 0)}
                  @input=${(ev: Event) => this._setField('taxAmount', Number((ev.target as HTMLInputElement).value))}>
                <div class="hint">Reports use amount − tax (net of GST).</div>
              </div>
              <div class="fg">
                <label>Reference</label>
                <input .value=${e.reference ?? ''}
                  @input=${(ev: Event) => this._setField('reference', (ev.target as HTMLInputElement).value)}>
              </div>
              <div class="fg full">
                <label>Remark</label>
                <textarea .value=${e.remark ?? ''}
                  @input=${(ev: Event) => this._setField('remark', (ev.target as HTMLTextAreaElement).value)}></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => (this._editing = null)}>Cancel</button>
            <button class="btn-submit" ?disabled=${this._saving} @click=${() => void this._save()}>
              ${this._saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.storeCode) return html`<div class="state">No outlet selected.</div>`;
    const t = this._totals;
    return html`
      <div class="toolbar">
        <div class="fg">
          <label>Period</label>
          <select .value=${this._preset}
            @change=${(ev: Event) => { this._applyPreset((ev.target as HTMLSelectElement).value); void this._load(); }}>
            ${MONTH_PRESETS.map((p) => html`<option value=${p.id} ?selected=${this._preset === p.id}>${p.label}</option>`)}
          </select>
        </div>
        <div class="fg">
          <label>From</label>
          <input type="date" .value=${this._from} @change=${(ev: Event) => { this._from = (ev.target as HTMLInputElement).value; this._preset = 'custom'; void this._load(); }}>
        </div>
        <div class="fg">
          <label>To</label>
          <input type="date" .value=${this._to} @change=${(ev: Event) => { this._to = (ev.target as HTMLInputElement).value; this._preset = 'custom'; void this._load(); }}>
        </div>
        <div class="fg">
          <label>Category</label>
          <select .value=${this._category} @change=${(ev: Event) => { this._category = (ev.target as HTMLSelectElement).value; void this._load(); }}>
            <option value="">All</option>
            ${(this._meta?.categories ?? []).map((c) => html`<option value=${c.id} ?selected=${this._category === c.id}>${c.name}</option>`)}
          </select>
        </div>
        <div class="fg">
          <label>Paid by</label>
          <select .value=${this._paidBy} @change=${(ev: Event) => { this._paidBy = (ev.target as HTMLSelectElement).value; void this._load(); }}>
            <option value="">All</option>
            ${(this._meta?.payBy ?? []).map((p) => html`<option value=${p.id} ?selected=${this._paidBy === p.id}>${p.name}</option>`)}
          </select>
        </div>
        <div class="fg grow">
          <label>Search</label>
          <input placeholder="Description, payee, reference…" .value=${this._q}
            @keydown=${(ev: KeyboardEvent) => { if (ev.key === 'Enter') void this._load(); }}
            @input=${(ev: Event) => (this._q = (ev.target as HTMLInputElement).value)}>
        </div>
        <button class="btn" @click=${() => void this._load()} ?disabled=${this._loading}>↻ Refresh</button>
        <button class="btn" @click=${() => this._newExpense()}>＋ New expense</button>
      </div>

      ${this._error ? html`<div class="error">${this._error}</div>` : ''}

      <div class="table-wrap">
        ${this._loading && !this._rows.length ? html`<div class="state">Loading expenses…</div>` : ''}
        ${!this._loading && this._loaded && !this._rows.length ? html`<div class="state">No expenses in this period.</div>` : ''}
        ${this._rows.length ? html`
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Category</th><th>Description</th><th>Paid by</th>
                <th class="num">Amount</th><th class="num">Tax</th><th class="num">Net</th>
                <th>Reference</th><th></th>
              </tr>
            </thead>
            <tbody>
              ${this._rows.map((r) => html`
                <tr>
                  <td>${msToIso(r.date)}</td>
                  <td>${r.categoryName}</td>
                  <td>
                    <div>${r.description}</div>
                    ${r.payee ? html`<div style="font-size:0.75rem;color:var(--color-text-secondary)">${r.payee}</div>` : ''}
                  </td>
                  <td>${r.paidByName || '—'}</td>
                  <td class="num">${fmtMoney(r.amount)}</td>
                  <td class="num">${r.taxAmount ? fmtMoney(r.taxAmount) : '—'}</td>
                  <td class="num">${fmtMoney(r.amount - r.taxAmount)}</td>
                  <td>${r.reference || '—'}</td>
                  <td>
                    <div class="actions">
                      <button class="btn-sm" @click=${() => this._edit(r)}>Edit</button>
                      ${this.isAdmin ? html`<button class="btn-sm" style="color:var(--color-error,#d93025)" @click=${() => void this._delete(r)}>Delete</button>` : ''}
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
            ${t ? html`
              <tfoot>
                <tr>
                  <td colspan="4">${t.count} expense${t.count === 1 ? '' : 's'}</td>
                  <td class="num">${fmtMoney(t.gross)}</td>
                  <td class="num">${t.tax ? fmtMoney(t.tax) : '—'}</td>
                  <td class="num">${fmtMoney(t.net)}</td>
                  <td colspan="2"></td>
                </tr>
              </tfoot>
            ` : ''}
          </table>
        ` : ''}

        ${this._rows.length ? html`
          <div class="cards">
            ${this._rows.map((r) => html`
              <div class="card">
                <div class="row1"><span>${r.categoryName}</span><span class="amt">RM ${fmtMoney(r.amount)}</span></div>
                <div class="row2"><span>${msToIso(r.date)} · ${r.paidByName || '—'}</span><span>${r.description}</span></div>
                <div class="actions" style="margin-top:0.4rem">
                  <button class="btn-sm" @click=${() => this._edit(r)}>Edit</button>
                  ${this.isAdmin ? html`<button class="btn-sm" style="color:var(--color-error,#d93025)" @click=${() => void this._delete(r)}>Delete</button>` : ''}
                </div>
              </div>
            `)}
            ${t ? html`<div class="card"><div class="row1"><span>Total (${t.count})</span><span class="amt">RM ${fmtMoney(t.gross)}</span></div></div>` : ''}
          </div>
        ` : ''}
      </div>

      ${this._editing ? this._renderForm() : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'expense-manage': ExpenseManage;
  }
}
